import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from '@/components/toast/toast';
import { Button } from '@/components/ui/button';
import { Loader2Icon, TicketPercentIcon } from 'lucide-react';
import { FormInput } from '@/components/form-input/form-input';
import styles from './personal-info.module.scss';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { newCertificate } from '@/use-cases/new-certificate';
import { dataEnrichment, OnboardingData } from '@/use-cases/data-enrichment';
import notifyHB from '@/config/notifyHB';
import {
	Certificate,
	CERTIFICATE_TYPE_LABELS,
} from '@/entities/certificate';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { getPaymentProducts } from '@/use-cases/get-payment-products';
import { PromoProduct, RecurrenteProduct } from '@/entities/payment';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	redeemPromoCode,
	mapPromoError,
} from '@/use-cases/redeem-promo-code';
import { CollapsibleSection } from '@/components/collapsible-section/collapsible-section';
import { PinFields } from '@/components/pin-fields/pin-fields';
import { PIN_REGEX, setPin as setPinRequest } from '@/use-cases/set-pin';
import { getErrorReason, trackEvent } from '@/utils/analytics';
import { isOnboardingConflict } from '@/utils/is-onboarding-conflict';

const DPI_REGEX = /^[0-9]{4}\s?[0-9]{5}\s?[0-9]{4}$/;
const PASSPORT_REGEX = /^[0-9]{8,9}$/;

type SectionId = 'pin' | 'personal' | 'certificate' | 'payment';

const CERT_TYPE_DESCRIPTIONS: Record<string, string> = {
	natural: 'Para firmar documentos a tu nombre, como persona particular.',
	legal_representative:
		'Para firmar en nombre de una empresa u organización que representas legalmente.',
	company:
		'Para que la empresa firme documentos con su propia identidad, por ejemplo certificaciones',
};

const SECTION_ORDER: SectionId[] = [
	'pin',
	'personal',
	'certificate',
	'payment',
];

type RegisterProps = {
	address: string;
	nextStep: () => void;
	setPersonName: (newName: string) => void;
	setNit: (newNit: string) => void;
	setDocumentNumber: (newNit: string) => void;
	setAddress: (newAddress: string) => void;
	setUserPhoneCountryCode: (newCode: string) => void;
	setUserNumber: (newPhoneNumber: string) => void;
	initialDocumentNumber?: string;
	initialNit?: string;
	setInitialBirthDate?: string;
	setInitialPhoneCountryCode?: string;
	setInitialPhoneNumber?: string;
	setCertificateType?: (type: Certificate['type']) => void;
	setAdditionalData?: (data: PersonalInfoFormData) => void;
	setSelectedProductId: (id: string) => void;
	setBirthDate?: (newBirthDate: string) => void;
	documentType?: OnboardingData['document_type'];
	setDocumentType?: (type: OnboardingData['document_type']) => void;
	// Values already entered when the user comes back from the summary.
	// The PIN is never passed in, so it is the only field asked again.
	initialCertificateType?: Certificate['type'];
	initialAdditionalData?: PersonalInfoFormData;
	initialSelectedProductId?: string;
	// Called when data-enrichment or the promo code answers 409.
	onConflict: () => void;
};

type LegalRepInfo = {
	company_name: string;
	company_tax_id: string;
	company_address: string;
	representation_capacity: string;
};

type ProfessionalInfo = {
	license_number: string;
	professional_association: string;
};

type EmployeeInfo = {
	company_name: string;
	company_department: string;
	company_address: string;
	company_tax_id: string;
	job_role: string;
};

type CompanyInfo = {
	company_name: string;
	company_department: string;
	company_address: string;
	company_tax_id: string;
};

type PublicOfificialInfo = {
	organization_name: string;
	organization_address: string;
	organization_phone: string;
	position: string;
};

// Index signature para permitir acceso dinámico por nombre de campo
export type AdditionalData =
	| (LegalRepInfo & { [key: string]: string })
	| (ProfessionalInfo & { [key: string]: string })
	| (EmployeeInfo & { [key: string]: string })
	| (CompanyInfo & { [key: string]: string })
	| (PublicOfificialInfo & { [key: string]: string });

export type PersonalInfoFormData = {
	legalRepInfo: LegalRepInfo;
	professionalInfo: ProfessionalInfo;
	employeeInfo: EmployeeInfo;
	companyInfo: CompanyInfo;
	publicOfficialInfo: PublicOfificialInfo;
};

export const QUERY_SIGNATURE_KEY = 'signature';
export const QUERY_SIGNATURE_NEW = 'new';

const PersonalInfo: React.FC<RegisterProps> = ({
	nextStep,
	setPersonName,
	setNit,
	setDocumentNumber,
	address,
	setAddress,
	setUserNumber,
	setUserPhoneCountryCode,
	initialDocumentNumber: initialDocumentNumber,
	initialNit,
	setInitialBirthDate,
	setInitialPhoneCountryCode,
	setInitialPhoneNumber,
	setCertificateType: setCertificateTypeProp,
	setAdditionalData,
	setSelectedProductId,
	setBirthDate,
	documentType: initialDocumentType,
	setDocumentType: setDocumentTypeProp,
	initialCertificateType,
	initialAdditionalData,
	initialSelectedProductId,
	onConflict,
}) => {
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const formatDateForInput = (dateString: string): string => {
		if (!dateString) return '';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '';
		return date.toISOString().split('T')[0];
	};

	const formatPhoneCountryCode = (code: string): string => {
		if (!code) return '';
		return code.replace(/^\+/, '');
	};

	const [isLoading, setIsLoading] = useState(false);
	const [identityDocumentNumber, setIdentityDocumentNumber] = useState('');
	const [nitNumber, setNitNumber] = useState(initialNit ?? '');
	const [pin, setPin] = useState('');
	const [pinConfirmation, setPinConfirmation] = useState('');
	const [openSection, setOpenSection] = useState<SectionId | null>('pin');
	const [tempDateValue, setTempDateValue] = useState(
		setInitialBirthDate ? formatDateForInput(setInitialBirthDate) : '',
	);
	const [phoneNumber, setPhoneNumber] = useState(setInitialPhoneNumber ?? '');
	const [documentType, setDocumentType] =
		useState<OnboardingData['document_type']>(initialDocumentType ?? 'DPI');
	const [phoneCountryCode, setPhoneCountryCode] = useState(
		setInitialPhoneCountryCode
			? formatPhoneCountryCode(setInitialPhoneCountryCode)
			: '',
	);
	const [certificateType, setCertificateType] = useState<
		Certificate['type'] | undefined
	>(initialCertificateType);

	const initialFormData = {
		legalRepInfo: {
			company_name: '',
			company_tax_id: '',
			company_address: '',
			representation_capacity: '',
		},
		professionalInfo: {
			license_number: '',
			professional_association: '',
		},
		employeeInfo: {
			company_name: '',
			company_department: '',
			company_address: '',
			company_tax_id: '',
			job_role: '',
		},
		companyInfo: {
			company_name: '',
			company_department: '',
			company_address: '',
			company_tax_id: '',
		},
		publicOfficialInfo: {
			organization_name: '',
			organization_address: '',
			organization_phone: '',
			position: '',
		},
	};

	const [formData, setFormData] = useState(
		initialAdditionalData ?? initialFormData,
	);
	const [products, setProducts] = useState<RecurrenteProduct[]>([]);
	const [productsStatus, setProductsStatus] = useState<
		'loading' | 'ready' | 'error'
	>('loading');
	const [selectedProductId, setSelectedProductIdLocal] = useState(
		initialSelectedProductId ?? '',
	);
	const [promoCode, setPromoCode] = useState('');
	const [promoError, setPromoError] = useState('');
	const [isPromoOpen, setIsPromoOpen] = useState(false);
	const [promoSuccessProduct, setPromoSuccessProduct] =
		useState<PromoProduct | null>(null);

	// The profile resolves after this component mounts, so seed every stored
	// value once it arrives. Each field is only filled while still empty, so
	// this never overwrites what the user has typed.
	useEffect(() => {
		if (initialDocumentNumber && !identityDocumentNumber) {
			setIdentityDocumentNumber(initialDocumentNumber);
		}
		if (initialNit && !nitNumber) {
			setNitNumber(initialNit);
		}
		if (setInitialBirthDate && !tempDateValue) {
			setTempDateValue(formatDateForInput(setInitialBirthDate));
		}
		if (setInitialPhoneCountryCode && !phoneCountryCode) {
			setPhoneCountryCode(
				formatPhoneCountryCode(setInitialPhoneCountryCode),
			);
		}
		if (setInitialPhoneNumber && !phoneNumber) {
			setPhoneNumber(setInitialPhoneNumber);
		}
	}, [
		initialDocumentNumber,
		initialNit,
		setInitialBirthDate,
		setInitialPhoneCountryCode,
		setInitialPhoneNumber,
	]);

	const loadProducts = () => {
		setProductsStatus('loading');
		getPaymentProducts()
			.then((loadedProducts) => {
				setProducts(loadedProducts);
				setProductsStatus('ready');
				// With a single plan there is no choice to make, so preselect it
				// instead of blocking Continuar behind a decorative radio.
				if (loadedProducts.length === 1) {
					handleProductSelect(loadedProducts[0].id);
				}
			})
			.catch((err) => {
				setProductsStatus('error');
				notifyHB('PersonalInfo', 'getPaymentProducts', err);
			});
	};

	useEffect(() => {
		loadProducts();
	}, []);

	const isCountryCode = (possibleCountryCode: string) => {
		return /^\d{1,3}$/.test(possibleCountryCode);
	};

	const isTelephoneNumber = (possibleNumber: string) => {
		return /^\(?\d{1,4}\)?\s?\d{1,4}\s?\d{1,4}$/.test(possibleNumber);
	};

	const getMissingPinFields = () => {
		const errors: string[] = [];
		if (!PIN_REGEX.test(pin)) errors.push('PIN de firma');
		else if (pin !== pinConfirmation) errors.push('Confirmación de PIN');
		return errors;
	};

	const getMissingPersonalFields = () => {
		const errors: string[] = [];
		if (!identityDocumentNumber) errors.push('DPI');
		if (!nitNumber) errors.push('NIT');
		if (!phoneCountryCode || !phoneNumber) errors.push('Teléfono');
		if (!address) errors.push('Dirección');
		return errors;
	};

	const getMissingPaymentFields = () => {
		const errors: string[] = [];
		if (!selectedProductId) errors.push('Producto');
		return errors;
	};

	const getMissingCertificateFields = () => {
		const errors: string[] = [];

		if (!certificateType) errors.push('Tipo de certificación');

		switch (certificateType) {
			case 'legal_representative': {
				const {
					company_name,
					company_tax_id,
					company_address,
					representation_capacity,
				} = formData.legalRepInfo;
				if (!company_name) errors.push('Nombre de persona jurídica');
				if (!company_tax_id) errors.push('NIT de persona jurídica');
				if (!company_address)
					errors.push('Dirección de persona jurídica');
				if (!representation_capacity)
					errors.push('Capacidad de representación');
				break;
			}
			case 'company': {
				const {
					company_name,
					company_department,
					company_address,
					company_tax_id,
				} = formData.companyInfo;
				if (!company_name) errors.push('Nombre de persona jurídica');
				if (!company_department) errors.push('Departamento');
				if (!company_address) errors.push('Dirección');
				if (!company_tax_id) errors.push('NIT');
				break;
			}
		}

		return errors;
	};

	const sectionErrors: Record<SectionId, string[]> = {
		pin: getMissingPinFields(),
		personal: getMissingPersonalFields(),
		certificate: getMissingCertificateFields(),
		payment: getMissingPaymentFields(),
	};

	const getMissingFields = () => SECTION_ORDER.flatMap((id) => sectionErrors[id]);

	const sendDataEnrichment = async () => {
		const errors = getMissingFields();
		if (errors.length > 0 || !certificateType) {
			const firstIncompleteSection = SECTION_ORDER.find(
				(id) => sectionErrors[id].length > 0,
			);
			if (firstIncompleteSection) setOpenSection(firstIncompleteSection);
			toast({ mode: 'caution', description: `Faltan los siguientes campos: ${errors.join(', ')}` });
			return;
		}

		try {
			setIsLoading(true);
			const requestData: OnboardingData = {
				document_number: identityDocumentNumber,
				document_type: documentType,
				phone_code: `+${phoneCountryCode}`,
				phone_number: `${phoneNumber}`,
				address: address,
				cert_type: certificateType,
				additional_data: formData,
			};
			const onboardingUlid = localStorage.getItem('onboarding-ulid');
			const dataEnrichmentResponse = await (query.get(
				QUERY_SIGNATURE_KEY,
			) !== QUERY_SIGNATURE_NEW && onboardingUlid
				? dataEnrichment(onboardingUlid, requestData)
				: newCertificate(requestData));

			const data = dataEnrichmentResponse;

			if (
				dataEnrichmentResponse.status === 200 ||
				dataEnrichmentResponse.status === 201
			) {
				if (data.OnboardingProcessUlid) {
					localStorage.setItem(
						'onboarding-ulid',
						data.OnboardingProcessUlid,
					);
				}
				try {
					await setPinRequest(pin);
				} catch (error) {
					// The PIN is not required to continue onboarding; it can be
					// configured later from /pin-setup.
					notifyHB('request-signature', 'PersonalInfo:setPin', error);
					toast({
						mode: 'caution',
						description:
							'No pudimos guardar tu PIN. Puedes configurarlo más tarde desde "Configurar PIN".',
					});
				}

				if (data.FullName) {
					setPersonName(data.FullName);
					setNit(nitNumber);
					setDocumentNumber(data.DPI);
					setUserNumber(phoneNumber);
					setUserPhoneCountryCode(phoneCountryCode);
					setBirthDate?.(tempDateValue);
					setDocumentTypeProp?.(documentType);

					// Save certificate info to parent state
					if (setCertificateTypeProp) {
						setCertificateTypeProp(certificateType);
					}
					if (setAdditionalData) {
						setAdditionalData(formData);
					}

					const resolvedUlid =
						data.OnboardingProcessUlid ?? (onboardingUlid ?? '');
					const code = promoCode.trim();
					if (code) {
						try {
							const { product } = await redeemPromoCode(
								resolvedUlid,
								code,
							);
							localStorage.setItem(
								'onboarding-payment-method',
								'promo',
							);
							setPromoSuccessProduct(product);
						} catch (error) {
							if (isOnboardingConflict(error)) {
								// Already paid: the backend says where to go.
								onConflict();
							} else if (selectedProductId) {
								notifyHB(
									'request-signature',
									'PersonalInfo',
									error,
								);
								// Promo failed but a product is selected:
								// fall back to the normal payment flow.
								localStorage.removeItem(
									'onboarding-payment-method',
								);
								nextStep();
							} else {
								notifyHB(
									'request-signature',
									'PersonalInfo',
									error,
								);
								setPromoError(mapPromoError(error));
							}
						}
					} else {
						localStorage.removeItem('onboarding-payment-method');
						nextStep();
					}
				} else {
					toast({ mode: 'caution', description: 'DPI inválido' });
					trackEvent('onboarding_error', {
						step: 'form',
						reason: 'invalid_dpi',
					});
				}
			} else {
				toast({ mode: 'caution', description: 'Datos inválidos' });
				trackEvent('onboarding_error', {
					step: 'form',
					reason: 'invalid_data',
				});
				// return false;
			}
		} catch (error: unknown) {
			if (isOnboardingConflict(error)) {
				// The process is paid or past the form: resume where it is.
				setIsLoading(false);
				onConflict();
				return;
			}
			toast({ mode: 'destructive', description: 'Ocurrió un error inesperado. Intenta de nuevo más tarde' });
			trackEvent('onboarding_error', {
				step: 'form',
				reason: `form_submit_${getErrorReason(error)}`,
			});
			notifyHB('request-signature', 'PersonalInfo', error);
			// return false;
		}

		setIsLoading(false);
	};

	const handleNextStep = async () => {
		await sendDataEnrichment();
	};

	const confirmStep = () => {
		handleNextStep().catch(console.debug);
	};

	const handlePromoModalContinue = () => {
		setPromoSuccessProduct(null);
		nextStep();
	};

	const handleCertTypeChange = (
		newValue: Exclude<typeof certificateType, undefined>,
	) => {
		setCertificateType(newValue);
		setFormData(initialFormData);
	};

	const handleProductSelect = (id: string) => {
		setSelectedProductIdLocal(id);
		setSelectedProductId(id);
	};

	const renderFields = (
		section: keyof typeof formData,
		fields: { label: string; name: string }[],
	) => {
		return fields.map((field) => (
			<FormInput
				key={field.name}
				inputLabel={field.label}
				value={(formData[section] as AdditionalData)[field.name]}
				setValue={(value) =>
					setFormData((prev) => ({
						...prev,
						[section]: {
							...prev[section],
							[field.name]: value,
						},
					}))
				}
				validInput={true}
			/>
		));
	};

	const handleProfessionalAssociationChange = (value: string) => {
		setFormData((prev) => ({
			...prev,
			professionalInfo: {
				...prev.professionalInfo,
				professional_association: value,
			},
		}));
	};

	const areFilledFieldsValid = () => {
		return (documentType === 'DPI' ? DPI_REGEX : PASSPORT_REGEX).test(
			identityDocumentNumber,
		);
	};

	const toggleSection = (id: SectionId) =>
		setOpenSection((current) => (current === id ? null : id));

	const completeSummary: Record<SectionId, () => string> = {
		pin: () => 'PIN configurado',
		// Echo the stored/prefilled values so the user can confirm them
		// without expanding the section.
		personal: () =>
			[
				identityDocumentNumber,
				nitNumber && `NIT ${nitNumber}`,
				phoneNumber && `+${phoneCountryCode} ${phoneNumber}`,
			]
				.filter(Boolean)
				.join(' · '),
		certificate: () => CERTIFICATE_TYPE_LABELS[certificateType!] ?? 'Completo',
		payment: () => {
			const productName = products.find(
				(product) => product.id === selectedProductId,
			)?.name;
			return [productName, promoCode.trim() && `código ${promoCode.trim()}`]
				.filter(Boolean)
				.join(' + ');
		},
	};

	const sectionSummary = (id: SectionId) => {
		const missing = sectionErrors[id];
		if (missing.length > 0) return `Faltan: ${missing.join(', ')}`;
		return completeSummary[id]() || 'Completo';
	};

	const sectionProps = (id: SectionId) => ({
		id,
		complete: sectionErrors[id].length === 0,
		open: openSection === id,
		onToggle: () => toggleSection(id),
		summary: sectionSummary(id),
	});

	return (
		<div className={styles['personal-info']}>
			<CollapsibleSection
				{...sectionProps('pin')}
				stepNumber={1}
				title="PIN de firma"
				description="Un solo PIN autoriza las firmas de todos tus certificados."
			>
				<PinFields
					pin={pin}
					setPin={setPin}
					pinConfirmation={pinConfirmation}
					setPinConfirmation={setPinConfirmation}
				/>
			</CollapsibleSection>

			<CollapsibleSection
				{...sectionProps('personal')}
				stepNumber={2}
				title="Información personal"
				description="Estos datos se incluirán en tu certificado."
			>
			<div className={`${styles['form-row']} ${styles['dpi-and-bday']}`}>
				<div className={styles['document-type']}>
					<Label>Tipo de Documento</Label>
					<RadioGroup
						value={documentType}
						onValueChange={(
							value: OnboardingData['document_type'],
						) => setDocumentType(value)}
						className={styles['document-type-options']}
					>
						<div className={styles['option']}>
							<RadioGroupItem value="DPI" id="DPI" />
							<Label htmlFor="DPI">DPI</Label>
						</div>
						<div className={styles['option']}>
							<RadioGroupItem value="PASSPORT" id="PASSPORT" />
							<Label htmlFor="PASSPORT">Pasaporte</Label>
						</div>
					</RadioGroup>
				</div>
				<FormInput
					inputType="number"
					inputLabel={'Documento de Identificación'}
					placeholder="Ingrese el número de su documento"
					value={identityDocumentNumber}
					setValue={setIdentityDocumentNumber}
					validInput={true}
				/>
				<FormInput
					inputType="date"
					autocomplete="bday"
					inputLabel={'Fecha de Nacimiento'}
					value={tempDateValue}
					setValue={setTempDateValue}
					validInput={true}
				/>
			</div>
			<span className={styles['form-row']}>
				<FormInput
					inputType="tel"
					inputLabel="Código"
					autocomplete="tel-country-code"
					placeholder={phoneCountryCode || '+XXX'}
					value={phoneCountryCode}
					setValue={setPhoneCountryCode}
					validInput={isCountryCode(phoneCountryCode)}
					maxWidth="100px"
				/>
				<FormInput
					inputType="tel"
					maxWidth={'300px'}
					inputLabel="Número de teléfono"
					autocomplete="tel-local"
					placeholder={phoneNumber || 'XXXX XXXX'}
					value={phoneNumber}
					setValue={setPhoneNumber}
					validInput={isTelephoneNumber(phoneNumber)}
				/>
				<FormInput
					inputType="number"
					maxWidth={'300px'}
					inputLabel="NIT"
					placeholder={'Ingrese su NIT'}
					value={nitNumber}
					setValue={setNitNumber}
					validInput={true}
				/>
			</span>
			<FormInput
				inputLabel={'Dirección'}
				value={address}
				autocomplete="street-address"
				placeholder={'Ingrese su dirección'}
				setValue={setAddress}
				validInput={true}
			/>
			</CollapsibleSection>

			<CollapsibleSection
				{...sectionProps('certificate')}
				stepNumber={3}
				title="Información de certificado"
				description="Elige el tipo de certificado que necesitas."
			>
			<div className={styles['certificate-type-row']}>
				<Label id="demo-simple-select-label">Tipo de certificado</Label>
				<Select
					value={certificateType}
					onValueChange={handleCertTypeChange}
				>
					<SelectTrigger>
						<SelectValue placeholder={'Selecciona un tipo'} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={'natural'}>
							Persona individual
						</SelectItem>
						<SelectItem value={'legal_representative'}>
							Representante legal
						</SelectItem>
						<SelectItem value={'company'}>
							Persona jurídica
						</SelectItem>
					</SelectContent>
				</Select>
				{certificateType && CERT_TYPE_DESCRIPTIONS[certificateType] && (
					<p className={styles['certificate-type-hint']}>
						{CERT_TYPE_DESCRIPTIONS[certificateType]}
					</p>
				)}
			</div>

			<>
				{certificateType === 'legal_representative' &&
					renderFields('legalRepInfo', [
						{
							label: 'Nombre de persona jurídica',
							name: 'company_name',
						},
						{
							label: 'NIT de persona jurídica',
							name: 'company_tax_id',
						},
						{
							label: 'Dirección de persona jurídica',
							name: 'company_address',
						},
						{
							label: 'Calidad de representación',
							name: 'representation_capacity',
						},
					])}
				{certificateType === 'company' &&
					renderFields('companyInfo', [
						{
							label: 'Nombre de persona jurídica',
							name: 'company_name',
						},
						{ label: 'Departamento', name: 'company_department' },
						{ label: 'Dirección', name: 'company_address' },
						{
							label: 'NIT de persona jurídica',
							name: 'company_tax_id',
						},
					])}
			</>
			</CollapsibleSection>

			<CollapsibleSection
				{...sectionProps('payment')}
				stepNumber={4}
				title="Producto y pago"
				description="Elige tu plan de firma."
			>
			{productsStatus === 'loading' && (
				<p className={styles['products-message']}>
					<Loader2Icon className="animate-spin" size={16} />
					Cargando productos...
				</p>
			)}
			{productsStatus === 'error' && (
				<div className={styles['products-message']}>
					<span>No pudimos cargar los productos disponibles.</span>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={loadProducts}
					>
						Reintentar
					</Button>
				</div>
			)}
			{productsStatus === 'ready' && products.length === 0 && (
				<p className={styles['products-message']}>
					No hay productos disponibles en este momento. Escríbenos a{' '}
					<a href="mailto:contacto@cybersign.gt">
						contacto@cybersign.gt
					</a>
					.
				</p>
			)}
			{productsStatus === 'ready' && products.length > 0 && (
				<span className={styles['products-legend']}>
					Selecciona tu plan
				</span>
			)}
			<RadioGroup
				value={selectedProductId}
				onValueChange={handleProductSelect}
				className={styles['product-selector']}
			>
				{products.map((product) => (
					<Label
						key={product.id}
						htmlFor={product.id}
						className={styles['product-option']}
					>
						<RadioGroupItem
							value={product.id}
							id={product.id}
							className={styles['product-radio']}
						/>
						<span className={styles['product-label']}>
							<span className={styles['product-name']}>{product.name}</span>
							<span className={styles['product-description']}>{product.description}</span>
							<span className={styles['product-price']}>
								{product.currency} {product.price.toFixed(2)}
							</span>
						</span>
					</Label>
				))}
			</RadioGroup>

			{!(isPromoOpen || promoCode || promoError) ? (
				<button
					type="button"
					className={styles['promo-toggle']}
					onClick={() => setIsPromoOpen(true)}
				>
					<TicketPercentIcon size={16} aria-hidden="true" />
					¿Tienes un código de descuento?
				</button>
			) : (
				<div className={styles['promo-box']}>
					<FormInput
						inputLabel={'Código de descuento (opcional)'}
						placeholder={'Ingresa tu código'}
						value={promoCode}
						setValue={(value) => {
							setPromoCode(value);
							setPromoError('');
						}}
						validInput={!promoError}
					/>
					<p className={styles['promo-hint']}>
						Se aplicará al continuar. Si no tienes uno, puedes dejarlo
						vacío.
					</p>
					{promoError && (
						<p className={styles['promo-error']} role="alert">
							{promoError}
						</p>
					)}
				</div>
			)}
			</CollapsibleSection>

			<Button
				color="primary"
				variant="default"
				onClick={confirmStep}
				disabled={
					isLoading ||
					(getMissingFields().length !== 0 && areFilledFieldsValid())
				}
			>
				{isLoading && <Loader2Icon className="animate-spin" />}
				Continuar
			</Button>

			<Dialog
				open={!!promoSuccessProduct}
				onOpenChange={(open) => {
					if (!open) handlePromoModalContinue();
				}}
			>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>Código aplicado</DialogTitle>
						<DialogDescription>
							Tu código promocional se aplicó correctamente.
						</DialogDescription>
					</DialogHeader>
					{promoSuccessProduct && (
						<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
							<dt className="text-muted-foreground">Producto</dt>
							<dd className="font-medium">
								{promoSuccessProduct.name}
							</dd>
							<dt className="text-muted-foreground">Vigencia</dt>
							<dd className="font-medium">
								{promoSuccessProduct.ttl}
							</dd>
							<dt className="text-muted-foreground">
								Firmas permitidas
							</dt>
							<dd className="font-medium">
								{promoSuccessProduct.usage_limit}
							</dd>
						</dl>
					)}
					<DialogFooter>
						<Button
							color="primary"
							variant="default"
							onClick={handlePromoModalContinue}
						>
							Continuar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default PersonalInfo;
